import { useState, useMemo } from "react";
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Clock, 
  Briefcase
} from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths, differenceInDays } from "date-fns";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { usePlan } from "@/hooks/usePlan";
import { useClientsForFilter, useProjectsForFilter } from "@/hooks/useReportsData";
import { useFinancialReport, useProjectReports, useClientReports, useTimeReport, useMonthlyComparisonReport } from "@/hooks/useReportsEngine";
import type { ReportFilters } from "@/lib/reports-engine";

// Import components
import { ReportsFilterBar } from "@/components/reports/ReportsFilterBar";
import { ReportsKPIStrip } from "@/components/reports/ReportsKPIStrip";
import { ExportButtons } from "@/components/reports/ExportButtons";
// ProFeatureUpsell removido - bloqueio de trial expirado feito no ProtectedRoute
import { FinancialReportTab } from "@/components/reports/FinancialReportTab";
import { ProfitabilityReportTab } from "@/components/reports/ProfitabilityReportTab";
import { ClientReportTab } from "@/components/reports/ClientReportTab";
import { TimeReportTab } from "@/components/reports/TimeReportTab";
import { ExecutiveReportTab } from "@/components/reports/ExecutiveReportTab";

export default function ReportsPage() {
  const { isPro, canExport } = usePlan();
  const [activeTab, setActiveTab] = useState("financial");
  
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  });

  // Previous period for comparison (same duration, previous period)
  const periodDays = differenceInDays(filters.endDate, filters.startDate) + 1;
  const prevFilters = useMemo(() => ({
    startDate: subMonths(filters.startDate, 1),
    endDate: subMonths(filters.endDate, 1),
  }), [filters.startDate, filters.endDate]);

  // Fetch filter options
  const { data: clientsOptions } = useClientsForFilter();
  const { data: projectsOptions } = useProjectsForFilter();

  // Fetch current period data
  const { data: financialData, isLoading: loadingFinancial } = useFinancialReport(filters);
  const { data: projectsData } = useProjectReports(filters);
  const { data: clientsData } = useClientReports(filters);
  const { data: timeData, isLoading: loadingTime } = useTimeReport(filters);

  // Fetch previous period data for comparison
  const { data: prevFinancialData } = useFinancialReport(prevFilters);
  const { data: prevTimeData } = useTimeReport(prevFilters);

  // KPI data
  const kpiData = {
    confirmedRevenue: financialData?.confirmedRevenue || 0,
    expectedRevenue: financialData?.expectedRevenue || 0,
    totalHours: financialData?.totalHours || 0,
    realHourlyRate: financialData?.realHourlyRate || 0,
    billablePercentage: financialData?.billablePercentage || 0,
    prevConfirmedRevenue: prevFinancialData?.confirmedRevenue || 0,
    prevExpectedRevenue: prevFinancialData?.expectedRevenue || 0,
    prevTotalHours: prevFinancialData?.totalHours || 0,
    prevRealHourlyRate: prevFinancialData?.realHourlyRate || 0,
    prevBillablePercentage: prevFinancialData?.billablePercentage || 0,
  };

  // Acesso completo - bloqueio é feito pelo ProtectedRoute
  return (
    <DashboardLayout title="Relatórios" subtitle="Centro de inteligência financeira">
      <div className="space-y-6">
        {/* Filter Bar */}
        <ReportsFilterBar
          filters={filters}
          onFiltersChange={setFilters}
          clients={clientsOptions || []}
          projects={projectsOptions || []}
          showPaymentStatus={activeTab === 'financial'}
        />

        {/* KPI Strip */}
        <ReportsKPIStrip 
          data={kpiData} 
          isLoading={loadingFinancial || loadingTime}
        />

        {/* Export buttons */}
        <div className="flex justify-end">
          <ExportButtons
            filters={filters}
            financial={financialData}
            projects={projectsData}
            clients={clientsData}
            timeReport={timeData}
            previousPeriod={prevFinancialData ? {
              revenue: prevFinancialData.confirmedRevenue,
              hourlyRate: prevFinancialData.realHourlyRate,
            } : undefined}
          />
        </div>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-5 h-auto gap-1 p-1 bg-muted/50">
            <TabsTrigger 
              value="financial" 
              className="flex items-center gap-1.5 py-2.5 px-3 text-xs sm:text-sm data-[state=active]:shadow-sm transition-all"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Financeiro</span>
              <span className="sm:hidden">Fin</span>
            </TabsTrigger>
            <TabsTrigger 
              value="profitability"
              className="flex items-center gap-1.5 py-2.5 px-3 text-xs sm:text-sm data-[state=active]:shadow-sm transition-all"
            >
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Rentabilidade</span>
              <span className="sm:hidden">Rent</span>
            </TabsTrigger>
            <TabsTrigger 
              value="clients"
              className="flex items-center gap-1.5 py-2.5 px-3 text-xs sm:text-sm data-[state=active]:shadow-sm transition-all"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Clientes</span>
              <span className="sm:hidden">Cli</span>
            </TabsTrigger>
            <TabsTrigger 
              value="time"
              className="flex items-center gap-1.5 py-2.5 px-3 text-xs sm:text-sm data-[state=active]:shadow-sm transition-all"
            >
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Tempo</span>
              <span className="sm:hidden">Tmp</span>
            </TabsTrigger>
            <TabsTrigger 
              value="executive"
              className="flex items-center gap-1.5 py-2.5 px-3 text-xs sm:text-sm data-[state=active]:shadow-sm transition-all"
            >
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">Executivo</span>
              <span className="sm:hidden">Exec</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="financial" className="mt-6">
            <FinancialReportTab filters={filters} />
          </TabsContent>

          <TabsContent value="profitability" className="mt-6">
            <ProfitabilityReportTab filters={filters} />
          </TabsContent>

          <TabsContent value="clients" className="mt-6">
            <ClientReportTab filters={filters} />
          </TabsContent>

          <TabsContent value="time" className="mt-6">
            <TimeReportTab filters={filters} />
          </TabsContent>

          <TabsContent value="executive" className="mt-6">
            <ExecutiveReportTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
